import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface CategoryWithCount {
  name: string;
  icon: string;
  count: number;
  slug: string;
}

export default function CategoriesSection() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<CategoryWithCount[]>([]);
  const [loading, setLoading] = useState(true);

  const categoryConfig = [
    {
      name: 'Placas de Vídeo',
      icon: 'ri-cpu-line',
      slug: 'placas-de-video'
    },
    {
      name: 'Processadores',
      icon: 'ri-cpu-fill',
      slug: 'processadores'
    },
    {
      name: 'Memória RAM',
      icon: 'ri-database-2-line',
      slug: 'memoria-ram'
    },
    {
      name: 'Armazenamento',
      icon: 'ri-hard-drive-2-line',
      slug: 'armazenamento'
    },
    {
      name: 'Placas-Mãe',
      icon: 'ri-circuit-board-line',
      slug: 'placas-mae'
    },
    {
      name: 'Fontes',
      icon: 'ri-flashlight-line',
      slug: 'fontes'
    },
    {
      name: 'Gabinetes',
      icon: 'ri-computer-line',
      slug: 'gabinetes'
    },
    {
      name: 'Periféricos',
      icon: 'ri-keyboard-line',
      slug: 'perifericos'
    }
  ];

  useEffect(() => {
    loadCategoriesWithCount();
  }, []);

  const loadCategoriesWithCount = async () => {
    try {
      // Buscar todas as categorias do banco
      const { data: dbCategories, error: categoriesError } = await supabase
        .from('categories')
        .select('*');

      if (categoriesError) throw categoriesError;

      // Buscar contagem de produtos por categoria
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('category')
        .eq('active', true);

      if (productsError) throw productsError;

      // Contar produtos por categoria (usando o nome da categoria)
      const productCounts: { [key: string]: number } = {};
      products?.forEach((product) => {
        if (product.category) {
          productCounts[product.category] = (productCounts[product.category] || 0) + 1;
        }
      });

      // Combinar configuração com dados reais
      const categoriesWithCount = categoryConfig.map((config) => {
        const dbCategory = dbCategories?.find(
          (cat) => cat.slug === config.slug || cat.name.toLowerCase() === config.name.toLowerCase()
        );
        
        // Contar usando o nome da categoria
        const count = dbCategory ? (productCounts[dbCategory.name] || 0) : 0;

        return {
          ...config,
          count
        };
      });

      setCategories(categoriesWithCount);
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
      // Em caso de erro, usar configuração padrão com contagem 0
      setCategories(categoryConfig.map(cat => ({ ...cat, count: 0 })));
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug: string) => {
    navigate(`/catalogo?category=${slug}`);
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-neon-cyan animate-spin"></i>
            <p className="mt-4 text-gray-400">Carregando categorias...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            Categorias em Destaque
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Explore nossa seleção completa de componentes e periféricos para montar o PC dos seus sonhos
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {categories.map((category) => (
            <div
              key={category.slug}
              onClick={() => handleCategoryClick(category.slug)}
              className="bg-gray-800 rounded-lg p-6 text-center border border-gray-700 hover:border-neon-cyan hover:shadow-neon-cyan transition-all transform hover:-translate-y-1 cursor-pointer group"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-neon-pink/20 to-neon-cyan/20 rounded-full flex items-center justify-center group-hover:from-neon-pink group-hover:to-neon-cyan transition-all group-hover:shadow-neon-pink">
                <i className={`${category.icon} text-3xl text-neon-cyan group-hover:text-white transition-colors`}></i>
              </div>
              <h3 className="font-semibold text-white mb-2">{category.name}</h3>
              <p className="text-sm text-gray-400">{category.count} produtos</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
